import type { Guide } from "./types.js";
import { Lead, H2, P, Ul, Li, AppLink, C, Callout } from "./primitives.js";

export const meta = {
  id: "cost-optimization",
  title: "Cost optimization: Bedrock toggle + model selection",
  category: "Workflows",
  lastUpdated: "2026-05-23",
  description: "How to route Claude spend to AWS credits, when to use Opus vs Sonnet vs Haiku, and where the heuristic-only design saves money.",
  relatedGuides: ["credentials-reference"],
  readTimeMin: 5,
} as const;

export function Body() {
  return (
    <>
      <Lead>
        Two cost levers in this app: (1) which Claude backend you draw from (Anthropic Direct vs AWS Bedrock — same per-token price, different budgets), and (2) which model the pipeline picks for each operation. Distribution is intentionally LLM-free at send time.
      </Lead>

      <H2>The Anthropic vs Bedrock toggle</H2>
      <P>
        Open <AppLink to="credentials">Credentials → Claude (Anthropic / Bedrock)</AppLink>. The <strong>Provider</strong> field is a real dropdown.
      </P>
      <Ul>
        <Li><strong>Anthropic Direct</strong> (default) — uses your <C>ANTHROPIC_API_KEY</C> balance.</Li>
        <Li><strong>AWS Bedrock</strong> — routes every Claude call through Bedrock at the same per-token price, debiting your AWS account (and any Bedrock credits). Requires <C>AWS_ACCESS_KEY_ID</C> + <C>AWS_SECRET_ACCESS_KEY</C> + a region with Claude (eu-central-1 closest to Israel).</Li>
      </Ul>
      <P>
        Toggling is hot — every Claude call in the pipeline picks up the new backend on the next request. No restart needed. Test button is provider-aware: it pings whichever backend is currently selected.
      </P>

      <Callout kind="warn" title="What the Max plan doesn't cover">
        Common misconception: the Claude Max plan covers the Claude Code CLI + Anthropic chat interfaces. It does NOT cover programmatic API calls via <C>@anthropic-ai/sdk</C>, which is what this pipeline uses. The Max balance and your <C>ANTHROPIC_API_KEY</C> balance are separate buckets.
      </Callout>

      <Callout kind="warn" title="Bedrock prompt caching">
        Prompt caching is verified on Anthropic Direct (Production engine reads ~21K cached tokens per article). On Bedrock the caching path works but is less battle-tested. After flipping to Bedrock, run one real article and measure the cache hit rate. If it drops materially, keep cache-heavy operations on direct + route only the others (classifier, expander, topic extractor) through Bedrock.
      </Callout>

      <H2>Model selection (which Claude does what)</H2>
      <table className="my-3 w-full table-auto text-sm">
        <thead className="text-2xs uppercase tracking-wider text-muted">
          <tr><th className="px-2 py-1 text-left">Operation</th><th className="px-2 py-1 text-left">Default model</th><th className="px-2 py-1 text-left">Cost/run</th></tr>
        </thead>
        <tbody className="divide-y divide-line">
          <tr><td className="px-2 py-1">Article production (standard)</td><td className="px-2 py-1">Sonnet 4.6</td><td className="px-2 py-1">~$0.23</td></tr>
          <tr><td className="px-2 py-1">Article production (pillar)</td><td className="px-2 py-1">Opus 4.6 (or 4.7 via flag)</td><td className="px-2 py-1">~$0.53</td></tr>
          <tr><td className="px-2 py-1">Brief generation</td><td className="px-2 py-1">Sonnet 4.6 (standard) / Opus (pillar)</td><td className="px-2 py-1">~$0.05</td></tr>
          <tr><td className="px-2 py-1">Cluster expansion (suggester)</td><td className="px-2 py-1">Sonnet 4.6</td><td className="px-2 py-1">~$0.02</td></tr>
          <tr><td className="px-2 py-1">Cluster classifier</td><td className="px-2 py-1">Haiku 4.5</td><td className="px-2 py-1">~$0.0001/keyword</td></tr>
          <tr><td className="px-2 py-1">Topic extractor (Content DB)</td><td className="px-2 py-1">Haiku 4.5</td><td className="px-2 py-1">~$0.0001/doc</td></tr>
          <tr><td className="px-2 py-1">CSV header mapper</td><td className="px-2 py-1">Haiku 4.5</td><td className="px-2 py-1">~$0.001/import</td></tr>
          <tr><td className="px-2 py-1">Cannibalization grouper</td><td className="px-2 py-1">Haiku 4.5</td><td className="px-2 py-1">~$0.001/batch</td></tr>
          <tr><td className="px-2 py-1">YouTube scope gate (discovery)</td><td className="px-2 py-1">Haiku 4.5</td><td className="px-2 py-1">~$0.0001/URL</td></tr>
        </tbody>
      </table>
      <P>Override per-call via <C>--model claude-opus-4-7</C> on the CLI or the model field in the brief/produce route bodies.</P>

      <H2>Why distribution is $0/article in LLM costs</H2>
      <P>
        Every channel orchestrator builds captions <strong>heuristically</strong> from the already-produced article + brief. No LLM call at send time.
      </P>
      <Ul>
        <Li>Caption recipe: <C>article.h1</C> + <C>firstSentences(articleMarkdown, charBudget)</C> + article URL.</Li>
        <Li>Hashtags come from <C>brief.semanticMustCover</C> (LLM-generated once at brief time, reused per-channel).</Li>
        <Li>An article distributed to all 8 channels still costs $0 in distribution LLM tokens.</Li>
      </Ul>
      <P>
        Trade-off: deterministic captions are all the same shape. If engagement metrics later show heuristic captions underperform, we can add a per-channel Haiku "smart-caption" pass (~$0.001/post). For now: cheap by design.
      </P>

      <H2>At-scale forecast</H2>
      <P>For the spec's 4-7 articles/week target:</P>
      <Ul>
        <Li><strong>Claude</strong>: ~$3-6/week. Article production dominates.</Li>
        <Li><strong>SerpAPI</strong>: free tier (250 searches/mo) or ~$50/mo paid.</Li>
        <Li><strong>DataForSEO</strong>: ~$50-200/mo when enabled.</Li>
        <Li><strong>Cluster expansion runs</strong>: ~$0.50-2/week.</Li>
        <Li><strong>Content DB incremental syncs</strong>: ~$0.10-0.50/week.</Li>
        <Li><strong>External subscriptions</strong>: only X API is paid ($200/mo). Everything else free or freemium.</Li>
      </Ul>
      <P>v2.1 §13.3 full-stack budget: ~$400-650/mo at scale.</P>

      <H2>Cheapest path forward</H2>
      <P>If you have $10K of unused AWS Bedrock credits sitting around:</P>
      <Ul>
        <Li>Enable Anthropic Claude models in AWS Console → Bedrock → Model access for <C>eu-central-1</C>.</Li>
        <Li>Flip the Provider toggle in Credentials.</Li>
        <Li>Run one real article ($0.23) to validate caching.</Li>
        <Li>If green: stay on Bedrock as default. Credits last ~12-18 months at the spec's target rate.</Li>
      </Ul>
    </>
  );
}

export const guide: Guide = { meta, Body };
